import { useEffect, useId } from 'react';

/**
 * Реестр форм с несохранёнными изменениями. Формы отмечают себя хуком `useUnsavedChangesGuard(dirty)`;
 * `UnsavedChangesGuard` в оболочке спрашивает подтверждение перед уходом со страницы
 * (переход по ссылке внутри CRM, закрытие или перезагрузка вкладки).
 */
const dirtySources = new Set<string>();
const listeners = new Set<() => void>();

function emit() {
  for (const l of listeners) l();
}

export function setUnsavedSource(id: string, dirty: boolean): void {
  const had = dirtySources.has(id);
  if (dirty && !had) dirtySources.add(id);
  else if (!dirty && had) dirtySources.delete(id);
  else return;
  emit();
}

export function hasUnsavedChanges(): boolean {
  return dirtySources.size > 0;
}

export function subscribeUnsavedChanges(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Сбросить все отметки (после подтверждённого ухода со страницы). */
export function clearUnsavedChanges(): void {
  if (dirtySources.size === 0) return;
  dirtySources.clear();
  emit();
}

/** Отметить форму как содержащую несохранённые изменения, пока `dirty === true`. */
export function useUnsavedChangesGuard(dirty: boolean): void {
  const id = useId();
  useEffect(() => {
    setUnsavedSource(id, dirty);
  }, [id, dirty]);
  useEffect(() => () => setUnsavedSource(id, false), [id]);
}

/**
 * Ссылка ведёт на другую страницу CRM (а не открывается в новой вкладке, не скачивание, не внешний сайт
 * и не та же страница) — значит, при несохранённых изменениях нужно подтверждение.
 */
export function isGuardedInAppNavigation(
  anchor: Pick<HTMLAnchorElement, 'href' | 'target'> & { hasAttribute(name: string): boolean },
  current: Pick<Location, 'origin' | 'pathname' | 'search'>
): boolean {
  if (anchor.target && anchor.target !== '_self') return false;
  if (anchor.hasAttribute('download')) return false;
  let url: URL;
  try {
    url = new URL(anchor.href, `${current.origin}${current.pathname}${current.search}`);
  } catch {
    return false;
  }
  if (url.origin !== current.origin) return false;
  return url.pathname !== current.pathname || url.search !== current.search;
}
